"use client";

import { useMemo, useState } from "react";
import Link from "next/link";
import { FadeIn, HomeEyebrow, HomeHeading, HomeLead, HomeSection } from "~/components/siteglue/home/section-shell";
import {
  BENCHMARK_DEFINITIONS,
  benchmarkLibraryHref,
  filterBenchmarkLibrary,
  type OptimizationService,
} from "~/lib/siteglue/benchmarks/catalog";

const FILTERS: { id: "all" | OptimizationService; label: string }[] = [
  { id: "all", label: "All" },
  { id: "aro", label: "ARO" },
  { id: "cro", label: "CRO" },
  { id: "aeo", label: "AEO" },
];

export function BenchmarkLibrary() {
  const [service, setService] = useState<"all" | OptimizationService>("all");
  const [query, setQuery] = useState("");
  const items = useMemo(() => filterBenchmarkLibrary({ service, query }), [service, query]);

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <HomeSection tone="brand" className="overflow-hidden pb-16 pt-16 sm:pb-20 sm:pt-24">
        <FadeIn className="relative mx-auto max-w-3xl text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#9cbb5a]">Resources</p>
          <h1 className="mt-4 text-balance text-4xl font-bold tracking-tight text-white sm:text-5xl">
            Audit Benchmark Library
          </h1>
          <p className="mx-auto mt-5 max-w-2xl text-lg leading-relaxed text-slate-300">
            The same versioned checks used by the scanner, reports, recommendations, dashboard, and Directory.
            Historical scans keep their original catalog version.
          </p>
        </FadeIn>
      </HomeSection>
      <HomeSection tone="white">
        <HomeEyebrow>Search and filter</HomeEyebrow>
        <HomeHeading>ARO, CRO, and AEO</HomeHeading>
        <HomeLead>
          {BENCHMARK_DEFINITIONS.length} published definitions. Planned AEO visibility checks stay labeled planned
          until live tests exist.
        </HomeLead>
        <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-center">
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search benchmarks"
            className="h-11 flex-1 rounded-full border border-slate-200 px-4 text-sm text-slate-900 outline-none focus:border-[#4f81bd]"
            aria-label="Search benchmarks"
          />
          <div className="flex flex-wrap gap-2">
            {FILTERS.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => setService(item.id)}
                className={`rounded-full px-3 py-1.5 text-sm font-semibold ${
                  service === item.id ? "bg-slate-900 text-white" : "border border-slate-200 text-slate-700"
                }`}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>
        <ul className="mt-10 grid gap-4 md:grid-cols-2">
          {items.map((item) => (
            <li key={item.id}>
              <Link
                href={benchmarkLibraryHref(item.slug)}
                className="block h-full rounded-2xl border border-slate-200 p-5 hover:border-slate-300"
              >
                <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                  {item.service.toUpperCase()}
                  {item.status === "planned" ? " · Planned" : ""}
                </p>
                <h2 className="mt-2 text-lg font-semibold text-slate-900">{item.name}</h2>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{item.what}</p>
                <p className="mt-3 text-xs text-slate-500">Version {item.version}</p>
              </Link>
            </li>
          ))}
        </ul>
      </HomeSection>
    </div>
  );
}
